import { Link } from "react-router-dom";
import { Star, ExternalLink, PhoneCall, Scale } from "lucide-react";
import { usePlatforms } from "@/lib/format";
import { useLead } from "@/components/layout/Layout";
import { CompareToggle, CompareTray, useCompareSelect } from "@/components/compare/CompareTray";
import { useFocusParam } from "@/lib/useFocus";
import { goUrl } from "@/lib/data";

const TITLES = { crypto: "Compare crypto exchanges in India", commodities: "Compare commodity (MCX) brokers", forex: "Compare forex (currency) brokers" };
const facts = (x) => (x.segment === "crypto"
  ? [["Spot fee", x.spot_fee], ["F&O fee", x.deriv_fee], ["Coins", `${x.coins}+`]]
  : [["Futures", x.fut_fee], ["Options", x.opt_fee], ["Exchanges", x.exchanges]]);

const PlatformCard = ({ x, sel }) => {
  const openLead = useLead();
  return (
    <div data-focus={x.id} data-testid={`platform-card-${x.id}`} className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-col hover:shadow-lg transition-shadow">
      <div className="flex items-start justify-between gap-2">
        <div><p className="font-display font-bold text-lg">{x.name}</p>{x.highlight && <p className="text-[11px] font-bold text-amber-700">{x.highlight}</p>}</div>
        <span className="inline-flex items-center gap-1 text-sm font-bold"><Star className="w-4 h-4 fill-amber-400 text-amber-400" />{x.rating}</span>
      </div>
      <dl className="mt-3 space-y-1 text-xs">{facts(x).map(([k, v]) => <div key={k} className="flex justify-between gap-3"><dt className="text-slate-500">{k}</dt><dd className="font-semibold text-right">{v}</dd></div>)}</dl>
      <p className="text-sm text-slate-600 mt-3 flex-1">{x.best_for}</p>
      <div className="mt-4 flex flex-wrap gap-2">
        <CompareToggle id={x.id} sel={sel} />
        <a href={goUrl(x.id, "card")} target="_blank" rel="noopener noreferrer" data-testid={`platform-site-${x.id}`} className="h-9 px-3 rounded-lg text-xs font-bold bg-[#10B981] hover:bg-[#059669] text-[#0F172A] inline-flex items-center gap-1.5">Visit site<ExternalLink className="w-3.5 h-3.5" /></a>
        <button data-testid={`platform-callback-${x.id}`} onClick={() => openLead({ id: x.id, company_name: x.name })} className="h-9 px-3 rounded-lg text-xs font-bold border border-slate-300 inline-flex items-center gap-1.5 hover:border-slate-500"><PhoneCall className="w-3.5 h-3.5" />Callback</button>
      </div>
    </div>
  );
};

export const PlatformCards = ({ segment }) => {
  const { data } = usePlatforms(segment);
  const sel = useCompareSelect();
  useFocusParam(!!data?.items?.length);
  const items = data?.items || [];
  const names = Object.fromEntries(items.map((x) => [x.id, x.name]));
  return (
    <section data-testid={`platforms-${segment}`} className="mt-10">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div><p className="font-display font-bold text-xl">{TITLES[segment]}</p><p className="text-sm text-slate-500">Tap “Compare” on 2–3 options to see them side by side.</p></div>
        <Link to={`/compare?type=${segment}`} data-testid={`platforms-compare-link-${segment}`} className="h-10 px-4 rounded-xl bg-[#1E293B] text-white text-sm font-bold inline-flex items-center gap-2"><Scale className="w-4 h-4" />Open comparison</Link>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{items.map((x) => <PlatformCard key={x.id} x={x} sel={sel} />)}</div>
      <p className="text-xs text-slate-500 mt-3">{data?.note}</p>
      <CompareTray sel={sel} type={segment} names={names} />
    </section>
  );
};
